import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { CapabilityProvider } from '@lemontaigu/platform-ui';
import { createSupabaseCapabilityStore } from '@lemontaigu/platform/adapters/client';
import { supabase } from './supabase.js';

/**
 * The capability composition root for the employee app (ADR-0013).
 *
 * ADDED 2026-07-28 as the prerequisite for F3 L2 increment 3. Step 8 mounted
 * this in `admin` and `reception` only — the two apps with controls to hide —
 * and `pms` got one with increment 1. This is the last of the four.
 * `useCapabilities()` THROWS without a provider, so gating C3's services before
 * mounting it would white-screen this app; typecheck cannot see that.
 *
 * INERT TODAY, deliberately. Nothing in this app reads the context yet, so all
 * this adds is one `my_capabilities()` call per session. Landing it separately
 * means increment 3 changes service code against a provider already proven to
 * mount, rather than doing both at once and having two suspects if it breaks.
 *
 * ⚠ WHAT THIS APP'S ACTOR LOOKS LIKE, and why increment 3 must not gate
 * naively. Its users are EMPLOYEES — a `staff_employees` row with
 * `auth_user_id` set and NO row in `public.staff`. Verified against the
 * post-0064 schema: such an account resolves to principals
 * `('role','employee')` + `('user',<uid>)` and holds exactly six capabilities —
 * `rh.board.read`, `rh.channel.read`, `rh.digest.read`, `rh.push.manage`,
 * `rh.roster.read`, `rh.timesheet.read_open`.
 *
 * Note what is ABSENT: `rh.timesheet.read`. An employee reads their own
 * timesheet through `employee_id = staff_employee_id()` — IDENTITY, not a
 * capability (migration 0062 draws exactly this distinction). So
 * `assertCapability(actor, RH_TIMESHEET_READ)` on the Pointages use case would
 * be STRICTER than the RLS policy and would lock every employee out of their
 * own hours. See the ladder §3.19 for the rule increment 3 must follow.
 */
export function AppCapabilityProvider({ children }: { children: ReactNode }) {
  const store = useMemo(() => createSupabaseCapabilityStore(supabase), []);
  return <CapabilityProvider store={store}>{children}</CapabilityProvider>;
}
